import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { useCurrency } from '../hooks/useCurrency';
import { useOrderBumps } from '../hooks/useOrderBumps';
import { Download, Smartphone, ShieldCheck, Gift, Check, Sparkles, ChevronDown } from 'lucide-react';
export function PricingSection() {
  const {
    formatPrice
  } = useCurrency();
  const {
    bump1,
    bump2,
    setBump1,
    setBump2,
    totalPrice,
    goToCheckout
  } = useOrderBumps();
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  return <section id="pricing" className="relative py-16 md:py-24 px-6 md:px-8 overflow-hidden" style={{
    background: 'linear-gradient(160deg, #2C2420 0%, #3D3229 45%, #4a3d34 100%)'
  }} data-id="element-318">
      {/* Ambient texture blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-terracotta/10 rounded-full blur-[100px] pointer-events-none" data-id="element-319" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sage/10 rounded-full blur-[100px] pointer-events-none" data-id="element-320" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-terracotta/5 rounded-full blur-[120px] pointer-events-none" data-id="element-321" />

      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.7,
      ease: 'easeOut'
    }} className="relative max-w-xl mx-auto text-center" data-id="element-322">
        {/* Pre-headline copy */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="mb-12" data-id="element-323">
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta/80 font-bold mb-6" data-id="element-324">
            Your reset starts here
          </p>
          <p className="font-serif text-3xl md:text-4xl text-cream/90 leading-snug mb-3" data-id="element-325">
            You don't need better naps.
          </p>
          <p className="font-serif text-4xl md:text-5xl text-cream font-medium leading-tight mb-8" data-id="element-326">
            You need <span className="italic text-terracotta" data-id="element-327">30 minutes.</span>
          </p>

          {/* Thin accent line */}
          <div className="w-12 h-px bg-terracotta/40 mx-auto mb-8" data-id="element-328" />

          <div className="space-y-4 mb-10" data-id="element-329">
            {[{
            text: 'A 7-day structure you can start today',
            highlight: true
          }, {
            text: 'A daily reset that protects your body and mind',
            highlight: true
          }, {
            text: '3 bonus tools to ask for help without guilt',
            highlight: true
          }].map((line, i) => <div key={i} className="flex items-center justify-center gap-3" data-id="element-330">
                <div className="w-1.5 h-1.5 rounded-full bg-terracotta/60 shrink-0" data-id="element-331" />
                <p className={`text-base md:text-lg ${line.highlight ? 'text-cream/80' : 'text-cream/50'}`} data-id="element-332">
                  {line.text}
                </p>
              </div>)}
          </div>

          <p className="text-cream/40 text-sm italic mb-10" data-id="element-333">
            Not cry-it-out. Not another sleep course. Just calm, clear
            structure.
          </p>
        </motion.div>

        {/* Premium Pricing Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative rounded-[2rem] overflow-hidden mb-8" style={{
        background: 'linear-gradient(180deg, rgba(250,247,242,0.08) 0%, rgba(250,247,242,0.03) 100%)',
        border: '1px solid rgba(250,247,242,0.15)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2), inset 0 1px 0 rgba(250,247,242,0.2)',
        backdropFilter: 'blur(20px)'
      }} data-id="element-334">
          {/* Animated top shimmer */}
          <motion.div animate={{
          x: ['-100%', '200%']
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 2
        }} className="absolute top-0 left-0 w-1/2 h-px bg-gradient-to-r from-transparent via-cream/50 to-transparent" data-id="element-335" />

          <div className="p-8 md:p-10" data-id="element-336">
            {/* Product Header */}
            <div className="mb-8" data-id="element-337">
              <div className="inline-flex items-center gap-1.5 bg-terracotta/20 border border-terracotta/30 text-terracotta text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4" data-id="element-338">
                <Sparkles className="w-3 h-3" data-id="element-339" />
                Instant Access
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-cream mb-2" data-id="element-340">
                The Nap-Trapped Reset
              </h3>
              <p className="text-cream/60 text-sm" data-id="element-341">
                A simple 7-day plan. A clear daily reset.
              </p>
            </div>

            {/* Value Breakdown */}
            <div className="mb-10 space-y-4 text-left bg-black/15 rounded-2xl p-6 border border-cream/5" data-id="element-342">
              <div className="flex justify-between items-center text-sm" data-id="element-343">
                <span className="text-cream/80 font-medium tracking-wide" data-id="element-344">
                  The Nap-Trapped Reset Guide
                </span>
                <span className="text-cream/40 font-serif italic line-through" data-id="element-345">
                  {formatPrice(35)} value
                </span>
              </div>
              <div className="flex justify-between items-center text-sm" data-id="element-346">
                <span className="text-cream/80 font-medium tracking-wide" data-id="element-347">
                  3 Premium Bonus Tools
                </span>
                <span className="text-cream/40 font-serif italic line-through" data-id="element-348">
                  {formatPrice(47)} value
                </span>
              </div>
              <div className="pt-4 border-t border-cream/10 flex justify-between items-center" data-id="element-349">
                <span className="text-cream/50 text-[10px] uppercase tracking-[0.2em] font-bold" data-id="element-350">
                  Total Value
                </span>
                <span className="text-cream/40 font-serif text-lg line-through" data-id="element-351">
                  {formatPrice(82)}
                </span>
              </div>
            </div>

            {/* Price Display */}
            <div className="mb-10 relative" data-id="element-352">
              <div className="absolute -top-5 right-0 md:right-4 bg-terracotta text-white text-[10px] font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full shadow-lg transform rotate-2" data-id="element-353">
                Save 60%
              </div>
              <div className="flex items-baseline justify-center gap-2 md:gap-3 mb-2" data-id="element-354">
                <span className="font-serif text-cream/50 text-lg md:text-2xl italic" data-id="element-355">
                  Only
                </span>
                <span className="font-serif text-5xl md:text-7xl font-bold text-cream tracking-tight leading-none drop-shadow-sm" data-id="element-356">
                  {formatPrice(14)}
                </span>
              </div>
              <p className="text-cream/40 text-xs uppercase tracking-widest font-medium" data-id="element-357">
                One-time payment · Yours forever
              </p>
            </div>

            {/* Bonus stack summary */}
            <div className="mb-8 rounded-2xl p-5 md:p-6 text-left relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, rgba(196,133,106,0.1) 0%, rgba(250,247,242,0.02) 100%)',
            border: '1px solid rgba(196,133,106,0.2)'
          }} data-id="element-358">
              <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-359" />

              <div className="flex items-center gap-2 mb-5 relative z-10" data-id="element-360">
                <Gift className="w-4 h-4 text-terracotta" data-id="element-361" />
                <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold" data-id="element-362">
                  Free Bonus Stack Included
                </p>
              </div>

              <div className="space-y-4 relative z-10" data-id="element-363">
                {[{
                name: 'The Body Back in 30 Reset Menu',
                image: 'https://cdn.magicpatterns.com/uploads/gj3HysKSw4wcCqLVvZKhfJ/bono1.png',
                description: 'When you finally get a window… the last thing you need is decision fatigue. This is a clean, pick-one menu of 12 simple options to use your 30 minutes on purpose—so you step back into the day lighter.',
                includes: ["12 'pick-1' reset options (shower, food, downshift, sunlight, stretch, etc.)", 'The simple 5–20–5 structure (land → reset → close)', "'Only got 10 minutes?' mini version", '1-minute Before/After check-in']
              }, {
                name: "The 'Someone Else's Arms' Handoff Guide",
                image: 'https://cdn.magicpatterns.com/uploads/htb81b6jSf8DQ4YtE2tBq8/bono2.png',
                description: "Support shouldn't require a 10-minute explanation… every time. This is the calm, one-sheet handoff system you can give to a grandparent, sitter, or friend—so you can step away for 30–60 minutes without spiraling.",
                includes: ['3 simple handoff sheets (different situations)', "'Before you leave the room' checklist", 'Ready-to-send texts to set expectations gently', 'Printable + phone-friendly version']
              }, {
                name: "The 'I Need Help' Text Script Kit",
                image: 'https://cdn.magicpatterns.com/uploads/gs7bBNjs5HcyXxCHdEFney/bono3.png',
                description: "Sometimes the hardest part isn't the nap. It's asking for help—without guilt, without over-explaining, without feeling like you have to justify it. This kit gives you 18 copy-paste messages you can send in 30 seconds.",
                includes: ['18 scripts (partner, grandparents, friend, sitter)', 'Simple message formula: time + task + duration + kind close', "Follow-ups that don't feel awkward", "'My non-negotiables' template (so your help actually helps)"]
              }].map((bonus, i) => <div key={i} className="flex items-center gap-3 bg-black/10 rounded-xl p-2 pr-4 border border-cream/5" data-id="element-364">
                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-cream/10 shadow-sm relative" data-id="element-365">
                      <img src={bonus.image} alt={bonus.name} className="w-full h-full object-cover" data-id="element-366" />
                    </div>
                    <span className="text-cream/80 text-sm leading-snug flex-1" data-id="element-367">
                      {bonus.name}
                    </span>
                    <span className="text-terracotta text-[10px] font-bold uppercase tracking-wider bg-terracotta/10 px-2 py-1 rounded" data-id="element-368">
                      Free
                    </span>
                  </div>)}
              </div>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-369">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden relative z-10" data-id="element-370">
                    <div className="pt-5 space-y-5" data-id="element-371">
                      {[{
                    name: 'The Body Back in 30 Reset Menu',
                    image: 'https://cdn.magicpatterns.com/uploads/gj3HysKSw4wcCqLVvZKhfJ/bono1.png',
                    description: 'When you finally get a window… the last thing you need is decision fatigue. This is a clean, pick-one menu of 12 simple options to use your 30 minutes on purpose—so you step back into the day lighter.',
                    includes: ["12 'pick-1' reset options (shower, food, downshift, sunlight, stretch, etc.)", 'The simple 5–20–5 structure (land → reset → close)', "'Only got 10 minutes?' mini version", '1-minute Before/After check-in']
                  }, {
                    name: "The 'Someone Else's Arms' Handoff Guide",
                    image: 'https://cdn.magicpatterns.com/uploads/htb81b6jSf8DQ4YtE2tBq8/bono2.png',
                    description: "Support shouldn't require a 10-minute explanation… every time. This is the calm, one-sheet handoff system you can give to a grandparent, sitter, or friend—so you can step away for 30–60 minutes without spiraling.",
                    includes: ['3 simple handoff sheets (different situations)', "'Before you leave the room' checklist", 'Ready-to-send texts to set expectations gently', 'Printable + phone-friendly version']
                  }, {
                    name: "The 'I Need Help' Text Script Kit",
                    image: 'https://cdn.magicpatterns.com/uploads/gs7bBNjs5HcyXxCHdEFney/bono3.png',
                    description: "Sometimes the hardest part isn't the nap. It's asking for help—without guilt, without over-explaining, without feeling like you have to justify it. This kit gives you 18 copy-paste messages you can send in 30 seconds.",
                    includes: ['18 scripts (partner, grandparents, friend, sitter)', 'Simple message formula: time + task + duration + kind close', "Follow-ups that don't feel awkward", "'My non-negotiables' template (so your help actually helps)"]
                  }].map((bonus, i) => <div key={i} className="rounded-2xl overflow-hidden border border-cream/8" style={{
                    background: 'linear-gradient(160deg, rgba(250,247,242,0.06) 0%, rgba(250,247,242,0.02) 100%)'
                  }} data-id="element-372">
                          {/* Mockup image */}
                          <div className="relative w-full aspect-[16/9] overflow-hidden" data-id="element-373">
                            <img src={bonus.image} alt={bonus.name} className="w-full h-full object-cover" data-id="element-374" />
                            <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-terracotta text-white px-2.5 py-1 rounded-full shadow-md" data-id="element-375">
                              Free
                            </span>
                          </div>

                          {/* Content */}
                          <div className="p-5" data-id="element-376">
                            <p className="text-terracotta text-xs font-bold uppercase tracking-wider mb-3" data-id="element-377">
                              {bonus.name}
                            </p>
                            <p className="text-cream/60 text-sm leading-relaxed mb-4" data-id="element-378">
                              {bonus.description}
                            </p>
                            <div className="space-y-2.5" data-id="element-379">
                              {bonus.includes.map((item, j) => <div key={j} className="flex items-start gap-2.5" data-id="element-380">
                                  <Check className="w-3.5 h-3.5 text-sage shrink-0 mt-0.5" data-id="element-381" />
                                  <span className="text-cream/70 text-xs leading-relaxed" data-id="element-382">
                                    {item}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Read more toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="flex items-center gap-1.5 mx-auto mt-5 text-terracotta/80 hover:text-terracotta text-xs font-semibold uppercase tracking-wider transition-colors duration-200 relative z-10" data-id="element-383">
                {showBonusDetails ? 'Read less' : 'Read more'}
                <motion.span animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-384">
                  <ChevronDown className="w-3.5 h-3.5" data-id="element-385" />
                </motion.span>
              </button>
            </div>

            {/* Emotional close */}
            <div id="pricing-cta" className="mb-8 space-y-1 text-cream/70 text-sm" data-id="element-386">
              <p data-id="element-387">You don't have to wait for this phase to pass.</p>
              <p className="text-cream font-medium" data-id="element-388">
                You can feel more stable today.
              </p>
            </div>

            {/* Payment info */}
            {/* Payment info with icons */}
            <div className="text-center mb-6" data-id="element-389">
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-390">
                <svg className="w-3 h-3 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-391">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-392" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-393" />
                </svg>
                <p className="text-cream/50 text-xs tracking-wide" data-id="element-394">
                  Secure checkout — pay in one tap
                </p>
              </div>
              <div className="flex items-center justify-center gap-3" data-id="element-395">
                {/* Shop Pay */}
                <div className="bg-cream/8 border border-cream/10 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-id="element-396">
                  <svg className="w-3.5 h-3.5 text-[#5A31F4]" viewBox="0 0 24 24" fill="currentColor" data-id="element-397">
                    <path d="M11.5 3.5c-2.3 0-4.1.8-5.3 2.4-.2.3-.1.6.1.8.3.2.6.1.8-.1 1-1.3 2.4-2 4.4-2 2.6 0 4.2 1.6 4.2 3.8 0 2.4-1.8 4.8-5.1 7.2l-.4.3c-.2.2-.3.5-.1.7.1.2.3.3.5.3h8.8c.3 0 .6-.3.6-.6s-.3-.6-.6-.6h-6.5c3.2-2.5 5-5.1 5-7.3 0-2.9-2.1-4.9-5.4-4.9z" data-id="element-398" />
                  </svg>
                  <span className="text-cream/60 text-[10px] font-semibold tracking-wide" data-id="element-399">
                    Shop Pay
                  </span>
                </div>
                {/* PayPal */}
                <div className="bg-cream/8 border border-cream/10 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-id="element-400">
                  <svg className="w-3.5 h-3.5 text-[#003087]" viewBox="0 0 24 24" fill="currentColor" data-id="element-401">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.603c2.678 0 4.578.956 5.227 3.07.25.82.303 1.558.154 2.382-.84 4.607-3.706 5.916-7.37 5.916H8.77a.77.77 0 0 0-.757.65l-1.06 6.248z" data-id="element-402" />
                  </svg>
                  <span className="text-cream/60 text-[10px] font-semibold tracking-wide" data-id="element-403">
                    PayPal
                  </span>
                </div>
                {/* Google Pay */}
                <div className="bg-cream/8 border border-cream/10 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-id="element-404">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" data-id="element-405">
                    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" data-id="element-406" />
                  </svg>
                  <span className="text-cream/60 text-[10px] font-semibold tracking-wide" data-id="element-407">
                    Google Pay
                  </span>
                </div>
              </div>
            </div>

            {/* Price reminder above CTA */}
            <div className="flex items-baseline justify-center gap-3 mb-5" data-id="element-408">
              <span className="font-serif text-cream/30 text-lg line-through" data-id="element-409">
                {formatPrice(35 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0))}
              </span>
              <span className="font-serif text-4xl md:text-5xl font-bold text-cream tracking-tight" data-id="element-410">
                {formatPrice(totalPrice)}
              </span>
              <span className="bg-terracotta text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ml-1 transform -translate-y-2" data-id="element-411">
                Save{' '}
                {Math.round((35 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0) - totalPrice) / (35 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0)) * 100)}
                %
              </span>
            </div>

            {/* CTA */}
            <div className="relative mb-6" data-id="element-412">
              {/* Subtle pulsing glow behind button */}
              <div className="absolute -inset-1 bg-terracotta/30 rounded-full blur-md animate-pulse" data-id="element-413" />
              <CTAButton text="Start My Gentle Reset" onClick={goToCheckout} fullWidth className="relative text-xl py-5" data-id="element-414" />
            </div>

            {/* Under-button trust */}
            <div className="flex flex-col items-center gap-2 mb-8" data-id="element-415">
              <span className="flex items-center gap-1.5 text-[10px] text-cream/35" data-id="element-416">
                <Download className="w-2.5 h-2.5" data-id="element-417" /> Instant download
                <span className="text-cream/20 mx-0.5" data-id="element-418">·</span>
                <Smartphone className="w-2.5 h-2.5" data-id="element-419" /> Phone-friendly
              </span>
              <span className="flex items-center gap-2 text-xs text-sage font-medium" data-id="element-420">
                <ShieldCheck className="w-3.5 h-3.5" data-id="element-421" /> 14-Day Gentle Guarantee
              </span>
            </div>

            {/* Order Bumps */}
            <div className="space-y-4" data-id="element-422">
              {/* Section header */}
              <div className="text-center mb-2" data-id="element-423">
                <div className="flex items-center gap-3 justify-center mb-2" data-id="element-424">
                  <div className="h-px w-8 bg-cream/15" data-id="element-425" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-terracotta/70 font-bold" data-id="element-426">
                    Enhance Your Reset
                  </p>
                  <div className="h-px w-8 bg-cream/15" data-id="element-427" />
                </div>
                <p className="text-cream/40 text-xs italic font-serif" data-id="element-428">
                  Most moms add at least one
                </p>
              </div>

              {/* Bump 1 — Emergency Calm Cards */}
              <div onClick={() => setBump1(!bump1)} className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 order-bump-shimmer" style={{
              background: bump1 ? 'linear-gradient(160deg, rgba(139,158,129,0.06) 0%, #FAF7F2 100%)' : '#FAF7F2',
              border: bump1 ? '1.5px solid #8B9E81' : '1px solid #E5DDD3',
              boxShadow: bump1 ? '0 4px 24px rgba(139,158,129,0.15), 0 0 0 1px rgba(139,158,129,0.1)' : '0 2px 12px rgba(61,50,41,0.04)'
            }} data-id="element-429">
                {/* Popular badge */}
                <div className="absolute top-3 right-3 z-10" data-id="element-430">
                  <span className="bg-terracotta text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-id="element-431">
                    Popular
                  </span>
                </div>

                {/* Eyebrow label */}
                <div className="px-5 pt-4 pb-0" data-id="element-432">
                  <div className="flex items-center gap-2 mb-3" data-id="element-433">
                    <svg className="w-3.5 h-3.5 text-[#8B9E81]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-434">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" data-id="element-435" />
                    </svg>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#8B9E81] font-bold" data-id="element-436">
                      Add-on
                    </span>
                  </div>
                </div>

                {/* Mockup image */}
                <div className="px-5 mb-4" data-id="element-437">
                  <div className="rounded-xl overflow-hidden" style={{
                  boxShadow: '0 6px 20px rgba(61,50,41,0.08)'
                }} data-id="element-438">
                    <img src="https://cdn.magicpatterns.com/uploads/dsM2NX64GWS1g4GnazJpeQ/mockup_1orderbump.png" alt="The Emergency Calm Cards" className="w-full h-auto object-cover" style={{
                    aspectRatio: '16/10'
                  }} data-id="element-439" />
                  </div>
                </div>

                {/* Content area */}
                <div className="px-5 pb-5" data-id="element-440">
                  {/* Checkbox + title row */}
                  <div className="flex items-start gap-3 mb-3" data-id="element-441">
                    <div className={`w-5 h-5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200 ${bump1 ? 'bg-[#8B9E81] border-[#8B9E81]' : 'border-[#C8BFB0] bg-white'}`} data-id="element-442">
                      {bump1 && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-443" />}
                    </div>
                    <p className="font-serif text-sm md:text-base font-bold text-[#3D3229] leading-snug flex-1" data-id="element-444">
                      YES — Add The Emergency Calm Cards for{' '}
                      <span className="text-[#999] line-through text-xs font-normal mr-1 font-serif italic" data-id="element-445">
                        {formatPrice(14)}
                      </span>
                      <span className="font-serif" data-id="element-446">{formatPrice(7)}</span>
                    </p>
                    <span className="inline-block bg-[#8B9E81] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap shrink-0 shadow-sm font-serif" data-id="element-447">
                      +{formatPrice(7)}
                    </span>
                  </div>

                  {/* Thin divider */}
                  <div className="h-px bg-[#E5DDD3]/60 mb-3 ml-8" data-id="element-448" />

                  {/* Description */}
                  <div data-id="element-449">
                    <p className="text-xs text-[#696969] leading-relaxed mb-3" data-id="element-450">
                      When naps go sideways, you don't need more advice — you
                      need an anchor. 10 minimalist Calm Cards you can save to
                      your phone for instant in-the-moment resets.
                    </p>
                    <div className="space-y-2 mb-3" data-id="element-451">
                      {['Stop the spiral fast', 'Designed for exhaustion', 'Instant access — use today'].map((point, j) => <div key={j} className="flex items-center gap-2.5" data-id="element-452">
                          <Check className="w-3 h-3 text-[#8B9E81] shrink-0" strokeWidth={2.5} data-id="element-453" />
                          <span className="text-[11px] text-[#525252] font-medium" data-id="element-454">
                            {point}
                          </span>
                        </div>)}
                    </div>
                    <p className="text-[11px] text-[#8B9E81] italic font-serif" data-id="element-455">
                      Your calm-down toolkit — always in your pocket.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bump 2 — Sanctuary Shields */}
              <div onClick={() => setBump2(!bump2)} className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 order-bump-shimmer" style={{
              background: bump2 ? 'linear-gradient(160deg, rgba(139,158,129,0.06) 0%, #FAF7F2 100%)' : '#FAF7F2',
              border: bump2 ? '1.5px solid #8B9E81' : '1px solid #E5DDD3',
              boxShadow: bump2 ? '0 4px 24px rgba(139,158,129,0.15), 0 0 0 1px rgba(139,158,129,0.1)' : '0 2px 12px rgba(61,50,41,0.04)'
            }} data-id="element-456">
                {/* Recommended badge */}
                <div className="absolute top-3 right-3 z-10" data-id="element-457">
                  <span className="bg-[#8B9E81] text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-id="element-458">
                    Recommended
                  </span>
                </div>

                {/* Eyebrow label */}
                <div className="px-5 pt-4 pb-0" data-id="element-459">
                  <div className="flex items-center gap-2 mb-3" data-id="element-460">
                    <svg className="w-3.5 h-3.5 text-[#8B9E81]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-461">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" data-id="element-462" />
                    </svg>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#8B9E81] font-bold" data-id="element-463">
                      Add-on
                    </span>
                  </div>
                </div>

                {/* Mockup image */}
                <div className="px-5 mb-4" data-id="element-464">
                  <div className="rounded-xl overflow-hidden" style={{
                  boxShadow: '0 6px 20px rgba(61,50,41,0.08)'
                }} data-id="element-465">
                    <img src="https://cdn.magicpatterns.com/uploads/uCcoLQh7Jfr5HPXNzSDmhW/mockup_2orderbump.png" alt="The Sanctuary Shields" className="w-full h-auto object-cover" style={{
                    aspectRatio: '16/10'
                  }} data-id="element-466" />
                  </div>
                </div>

                {/* Content area */}
                <div className="px-5 pb-5" data-id="element-467">
                  {/* Checkbox + title row */}
                  <div className="flex items-start gap-3 mb-3" data-id="element-468">
                    <div className={`w-5 h-5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200 ${bump2 ? 'bg-[#8B9E81] border-[#8B9E81]' : 'border-[#C8BFB0] bg-white'}`} data-id="element-469">
                      {bump2 && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-470" />}
                    </div>
                    <p className="font-serif text-sm md:text-base font-bold text-[#3D3229] leading-snug flex-1" data-id="element-471">
                      YES — Add The Sanctuary Shields for{' '}
                      <span className="text-[#999] line-through text-xs font-normal mr-1 font-serif italic" data-id="element-472">
                        {formatPrice(8)}
                      </span>
                      <span className="font-serif" data-id="element-473">{formatPrice(3)}</span>
                    </p>
                    <span className="inline-block bg-[#8B9E81] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap shrink-0 shadow-sm font-serif" data-id="element-474">
                      +{formatPrice(3)}
                    </span>
                  </div>

                  {/* Thin divider */}
                  <div className="h-px bg-[#E5DDD3]/60 mb-3 ml-8" data-id="element-475" />

                  {/* Description */}
                  <div data-id="element-476">
                    <p className="text-xs text-[#696969] leading-relaxed mb-3" data-id="element-477">
                      4 elegant printable signs to prevent knocks, doorbells and
                      interruptions mid-transfer. Protect the nap. Protect your
                      peace.
                    </p>
                    <div className="space-y-2 mb-3" data-id="element-478">
                      {['Stops interruptions before they happen', 'Partner-ready — no explanation needed', 'Pretty enough to leave up permanently'].map((point, j) => <div key={j} className="flex items-center gap-2.5" data-id="element-479">
                          <Check className="w-3 h-3 text-[#8B9E81] shrink-0" strokeWidth={2.5} data-id="element-480" />
                          <span className="text-[11px] text-[#525252] font-medium" data-id="element-481">
                            {point}
                          </span>
                        </div>)}
                    </div>
                    <p className="text-[11px] text-[#8B9E81] italic font-serif" data-id="element-482">
                      One less thing to worry about during nap time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary — always visible */}
            <div className="mt-6 rounded-2xl p-4 md:p-6 lg:p-7 text-left relative overflow-hidden" style={{
            background: 'linear-gradient(160deg, rgba(250,247,242,0.16) 0%, rgba(250,247,242,0.07) 100%)',
            border: '1px solid rgba(250,247,242,0.18)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 6px rgba(0,0,0,0.06)'
          }} data-id="element-483">
              {/* Subtle glow */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-sage/10 rounded-full blur-2xl pointer-events-none" data-id="element-484" />

              {/* Header */}
              <div className="flex items-center gap-2.5 mb-5 relative z-10 pb-3 border-b border-cream/10" data-id="element-485">
                <div className="w-7 h-7 rounded-lg bg-cream/10 flex items-center justify-center" data-id="element-486">
                  <svg className="w-3.5 h-3.5 text-cream/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-487">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" data-id="element-488" />
                    <line x1="3" y1="6" x2="21" y2="6" data-id="element-489" />
                    <path d="M16 10a4 4 0 0 1-8 0" data-id="element-490" />
                  </svg>
                </div>
                <p className="text-cream/70 text-xs uppercase tracking-[0.18em] font-bold" data-id="element-491">
                  Your Order
                </p>
              </div>

              <div className="space-y-2 relative z-10" data-id="element-492">
                {/* Base product */}
                <div className="flex justify-between items-start text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.07]" data-id="element-493">
                  <div className="flex items-start gap-2 min-w-0" data-id="element-494">
                    <div className="w-1.5 h-1.5 rounded-full bg-terracotta/60 shrink-0 mt-1.5" data-id="element-495" />
                    <span className="font-medium text-[13px] md:text-[15px] leading-tight" data-id="element-496">
                      The Nap-Trapped Reset + 3 Bonuses
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-497">
                    <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-498">
                      {formatPrice(35)}
                    </span>
                    <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-499">
                      {formatPrice(14)}
                    </span>
                  </div>
                </div>

                {/* Bump 1 */}
                <AnimatePresence data-id="element-500">
                  {bump1 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="flex justify-between items-center text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.04]" data-id="element-501">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-502">
                        <div className="w-1.5 h-1.5 rounded-full bg-sage/60 shrink-0" data-id="element-503" />
                        <span className="font-medium text-[13px] md:text-[15px]" data-id="element-504">
                          Emergency Calm Cards
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-505">
                        <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-506">
                          {formatPrice(14)}
                        </span>
                        <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-507">
                          {formatPrice(7)}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Bump 2 */}
                <AnimatePresence data-id="element-508">
                  {bump2 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="flex justify-between items-center text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.07]" data-id="element-509">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-510">
                        <div className="w-1.5 h-1.5 rounded-full bg-sage/60 shrink-0" data-id="element-511" />
                        <span className="font-medium text-[13px] md:text-[15px]" data-id="element-512">
                          Sanctuary Shields
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-513">
                        <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-514">
                          {formatPrice(8)}
                        </span>
                        <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-515">
                          {formatPrice(3)}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Total row */}
                <div className="flex justify-between items-center mt-3 py-4 px-4 md:py-5 md:px-5 rounded-xl" style={{
                background: 'linear-gradient(135deg, rgba(139,158,129,0.15) 0%, rgba(139,158,129,0.08) 100%)',
                border: '1px solid rgba(139,158,129,0.2)'
              }} data-id="element-516">
                  <span className="text-cream/90 font-bold text-sm md:text-base uppercase tracking-widest" data-id="element-517">
                    Total
                  </span>
                  <div className="flex items-baseline gap-3 md:gap-4" data-id="element-518">
                    <span className="font-serif text-cream/30 text-sm md:text-base italic line-through" data-id="element-519">
                      {formatPrice(35 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0))}
                    </span>
                    <span className="font-serif text-cream font-bold text-xl md:text-3xl tracking-tight" data-id="element-520">
                      {formatPrice(totalPrice)}
                    </span>
                  </div>
                </div>

                {/* Savings badge */}
                <div className="flex justify-center pt-4" data-id="element-521">
                  <motion.span key={totalPrice} initial={{
                  scale: 0.95,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.3,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="inline-flex items-center gap-2 bg-[#8B9E81]/15 border border-[#8B9E81]/30 text-[#8B9E81] text-[11px] uppercase tracking-widest font-bold px-5 py-2 rounded-full shadow-sm" data-id="element-522">
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-523" />
                    You save{' '}
                    <span className="font-serif text-sm tracking-normal" data-id="element-524">
                      {formatPrice(35 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0) - totalPrice)}
                    </span>
                  </motion.span>
                </div>
              </div>
            </div>

            {/* Checkout button below order summary */}
            <motion.button onClick={goToCheckout} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="mt-5 w-full py-4 md:py-5 rounded-2xl font-bold text-base md:text-lg tracking-wide text-cream flex items-center justify-center gap-2.5 transition-all duration-300 cursor-pointer" style={{
            background: 'linear-gradient(135deg, #7B8E6B 0%, #657556 100%)',
            boxShadow: '0 4px 20px rgba(123,142,107,0.3), 0 1px 4px rgba(0,0,0,0.1)'
          }} data-id="element-525">
              Complete My Order
              <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-526">
                <path d="M5 12h14" data-id="element-527" />
                <path d="m12 5 7 7-7 7" data-id="element-528" />
              </svg>
            </motion.button>
          </div>
        </motion.div>

        {/* Guarantee block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="rounded-2xl p-6 text-center" style={{
        background: 'rgba(123,142,107,0.08)',
        border: '1px solid rgba(123,142,107,0.15)'
      }} data-id="element-529">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-530">
            <ShieldCheck className="w-4 h-4 text-sage" data-id="element-531" />
            <p className="text-sm font-bold text-cream/80 uppercase tracking-widest" data-id="element-532">
              14-Day Gentle Guarantee
            </p>
          </div>
          <p className="text-cream/50 text-sm leading-relaxed" data-id="element-533">
            Try it. If you don't feel more supported and regulated, just reach
            out.
            <br data-id="element-534" />
            <span className="text-cream/70" data-id="element-535">No stress. No shame.</span>
          </p>
        </motion.div>
      </motion.div>
    </section>;
}